import React, { useState } from 'react';

const CRACKS = [
  {
    id: 'gravity',
    title: 'Gravity',
    hebrewTitle: 'הַמְּשִׁיכָה הַגְּדוֹלָה',
    status: 'Unsolved',
    color: 'rgba(255,255,255,0.6)',
    science: 'Gravity is the only fundamental force not described by the Standard Model. Every attempt to quantize gravity — to make it fit the quantum framework — fails or produces infinities. General relativity (Einstein) and quantum mechanics are both extraordinarily accurate in their domains. But they are mathematically incompatible. No one knows why.',
    ruach: 'Gravity draws all things toward each other across infinite distance — no boundary, no limit, no mediating particle we can detect. It is the force most like unconditional presence. "Where were you when I laid the earth\'s foundation? Tell me, if you understand." — Job 38:4. The force that holds galaxies together remains beyond our model — a daily reminder that the map is not the territory.',
    scripture: 'Job 38:4',
    implications: 'Without quantum gravity, we cannot describe what happened at the Big Bang — the Planck epoch — or inside black holes. The deepest moments of creation are inaccessible to current physics.',
    ruachImplication: 'The moment of Tohu va-Bohu — תֹהוּ וָבֹהוּ — is precisely where our model fails. Creation\'s first instant is, by physics, unknowable. This is not a gap to be embarrassed about. It is an invitation.',
  },
  {
    id: 'darkmatter',
    title: 'Dark matter',
    hebrewTitle: 'חֹשֶׁךְ נִסְתָּר',
    status: 'Unknown',
    color: '#9060ff',
    science: 'Galaxies rotate wrong. Stars at the outer edges of galaxies move too fast — as if there is far more mass than we can see. Something — "dark matter" — makes up ~27% of the universe\'s total energy content. It does not emit, absorb, or reflect light. It interacts gravitationally but not electromagnetically. It is not any particle in the Standard Model.',
    ruach: '"Darkness was over the surface of the deep." — Genesis 1:2. Before light was spoken, something filled the void. Dark matter — חֹשֶׁךְ נִסְתָּר, hidden darkness — holds galaxies together invisibly. It shaped the cosmic web that became the universe we see. Something that does not interact with light shaped everything that light illuminates.',
    scripture: 'Genesis 1:2',
    implications: '27% of the universe is made of something completely outside our model. The Standard Model describes only ~5% of the universe\'s total energy content.',
    ruachImplication: 'That which is invisible holds what is visible together. The unseen scaffolding of creation. "Faith is confidence in what we hope for and assurance about what we do not see." — Hebrews 11:1',
  },
  {
    id: 'darkenergy',
    title: 'Dark energy',
    hebrewTitle: 'כֹּחַ הַהִתְרַחֲקוּת',
    status: 'Unknown',
    color: '#40a0ff',
    science: 'The universe is not just expanding — it is accelerating in its expansion. Something is pushing space itself apart faster and faster. This "dark energy" makes up ~68% of the universe\'s total energy content. It may be a property of space itself (a cosmological constant), or a dynamic field, or something else entirely. The Standard Model has no explanation for it.',
    ruach: 'Creation is still expanding — still being spoken outward. "The heavens declare the glory of God; the skies proclaim the work of his hands." — Psalm 19:1. The universe does not coast. It accelerates. Something continues to push creation outward — a force that acts on space itself, not on the things within it.',
    scripture: 'Psalm 19:1',
    implications: '68% of the universe is made of something that accelerates its expansion. Combined with dark matter, 95% of the universe is outside our current model.',
    ruachImplication: 'If 95% of reality is beyond our current model, what does that say about how much of YHWH\'s creation we actually understand? The Standard Model is extraordinary — and it describes 5% of what exists.',
  },
  {
    id: 'antimatter',
    title: 'Matter/antimatter asymmetry',
    hebrewTitle: 'אֲסִימֶטְרִיָּה',
    status: 'Partially understood',
    color: '#ff6090',
    science: 'At the Big Bang, matter and antimatter should have been created in equal amounts. Equal amounts would have annihilated each other completely — leaving a universe of pure radiation with nothing. Yet here we are. Something caused a tiny excess of matter — about 1 extra proton per billion — and that excess became everything. The Standard Model predicts CP violation (matter/antimatter asymmetry) but not nearly enough to explain our existence.',
    ruach: 'Creation chose form over dissolution. One part in a billion survived — and from that improbable surplus came stars, galaxies, planets, life, and thought. "You are precious and honored in my sight." — Isaiah 43:4. Your existence traces directly to an asymmetry so small it should have been rounded away. It was not.',
    scripture: 'Isaiah 43:4',
    implications: 'The Standard Model\'s CP violation is too small by a factor of billions to explain why anything exists. New physics must be responsible for our existence.',
    ruachImplication: 'The universe did not have to produce matter. Physics almost didn\'t allow it. Your existence — every atom of you — is the result of a primordial preference for form over void that our best model cannot fully explain.',
  },
  {
    id: 'neutrino',
    title: 'Neutrino mass',
    hebrewTitle: 'מִשְׁקַל הַנְּשִׁימָה',
    status: 'Beyond Standard Model',
    color: '#50e0a0',
    science: 'The Standard Model predicts neutrinos have exactly zero mass. Neutrino oscillation experiments — observing neutrinos change "flavor" as they travel — prove they have non-zero mass. This is direct experimental proof that the Standard Model is incomplete. The mechanism by which neutrinos acquire their tiny mass (Seesaw mechanism? Majorana mass?) is unknown.',
    ruach: '"The wind blows wherever it pleases. You hear its sound, but you cannot tell where it comes from or where it is going." — John 3:8. The breath whose weight we cannot measure. Neutrinos pass through everything, change their identity as they travel, and have mass our model says they shouldn\'t. They are the particle that most resists our frameworks.',
    scripture: 'John 3:8',
    implications: 'Neutrino mass is the clearest experimental proof that physics beyond the Standard Model exists. The mechanism is unknown but real.',
    ruachImplication: 'The hidden breath has weight. We know it must — but we cannot fully account for it. Some things that are real exceed our ability to measure or explain them.',
  },
  {
    id: 'hierarchy',
    title: 'The hierarchy problem',
    hebrewTitle: 'בְּעָיַת הַהִיֵּרַרְכְיָה',
    status: 'Unsolved',
    color: '#f0c040',
    science: 'The Higgs boson mass is 125 GeV. Quantum corrections from virtual particles should push this mass up to the Planck scale (10¹⁹ GeV) — a factor of 10¹⁷ larger. To get 125 GeV, there must be an almost perfect cancellation between enormous positive and negative corrections — fine-tuned to 1 part in 10³⁴. This extreme fine-tuning feels deeply unnatural. Supersymmetry was proposed to solve it — but has not been found.',
    ruach: 'The Higgs mass is precisely what it needs to be for atoms, chemistry, and life to exist. If it were significantly different, the universe would be unrecognizable. The fine-tuning required is staggering — and unexplained. "Are not two sparrows sold for a penny? Yet not one of them will fall to the ground outside your Father\'s care." — Matthew 10:29. The precision of creation\'s parameters is not accidental in any framework that takes meaning seriously.',
    scripture: 'Matthew 10:29',
    implications: 'The Higgs mass appears fine-tuned to a degree that has no natural explanation in the Standard Model. This is one of the strongest motivations for new physics.',
    ruachImplication: 'The Ruach field\'s strength — its vacuum expectation value — is precisely calibrated. Too strong and all matter would be too heavy for atoms. Too weak and it would be too light. The precision points beyond itself.',
  },
];

const STATUS_COLORS = {
  'Unsolved': '#ff6060',
  'Unknown': '#9060ff',
  'Partially understood': '#f0c040',
  'Beyond Standard Model': '#50e0a0',
};

export default function StandardModelCracks({ mode }) {
  const [selected, setSelected] = useState(null);
  const isRuach = mode === 'ruach';
  const accentColor = isRuach ? '#ffb060' : '#f0c040';

  return (
    <div>
      <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.75rem', letterSpacing: '0.1em', marginBottom: '1rem', fontFamily: 'Space Mono', textTransform: 'uppercase' }}>
        {isRuach ? 'Where knowing ends and mystery begins' : 'Where the Standard Model breaks down'}
      </p>

      {/* Summary banner */}
      <div style={{
        padding: '1rem 1.25rem', borderRadius: '10px', marginBottom: '1.5rem',
        background: isRuach ? 'rgba(255,160,60,0.08)' : 'rgba(240,192,64,0.08)',
        border: `1px solid ${accentColor}30`,
      }}>
        <p style={{ fontSize: '0.83rem', color: 'rgba(255,255,255,0.65)', lineHeight: 1.75, margin: 0 }}>
          {isRuach
            ? <>"For now we see only a reflection as in a mirror; then we shall see face to face." — 1 Corinthians 13:12. The Standard Model describes ~<strong style={{ color: accentColor }}>5% of the universe</strong>. The remaining 95% — dark matter, dark energy, and forces we cannot yet quantize — points to something far larger than our current model. These are not failures. They are <em>invitations</em>.</>
            : <>The Standard Model is the most precisely tested theory in human history. It also describes only <strong style={{ color: accentColor }}>~5% of the universe's total energy content</strong>. Dark matter (~27%) and dark energy (~68%) are completely outside it. These six open problems are where physics reaches its current edge.</>
          }
        </p>
      </div>

      {/* Crack cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {CRACKS.map(crack => (
          <div
            key={crack.id}
            onClick={() => setSelected(selected?.id === crack.id ? null : crack)}
            style={{
              borderRadius: '10px', overflow: 'hidden', cursor: 'pointer',
              border: `1px solid ${selected?.id === crack.id ? crack.color + '60' : 'rgba(255,255,255,0.08)'}`,
              background: selected?.id === crack.id ? `${crack.color}08` : 'rgba(255,255,255,0.02)',
              transition: 'all 0.2s',
            }}
          >
            {/* Header row */}
            <div style={{ padding: '0.875rem 1rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: crack.color, flexShrink: 0, boxShadow: selected?.id === crack.id ? `0 0 8px ${crack.color}` : 'none' }} />
                <div>
                  <div style={{ fontSize: '0.9rem', fontWeight: 600, color: '#fff', fontFamily: 'Syne' }}>
                    {isRuach ? crack.hebrewTitle : crack.title}
                  </div>
                  {isRuach && (
                    <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)' }}>{crack.title}</div>
                  )}
                </div>
              </div>
              <div style={{
                fontSize: '0.62rem', padding: '2px 8px', borderRadius: '20px', fontFamily: 'Space Mono',
                background: `${STATUS_COLORS[crack.status]}18`,
                color: STATUS_COLORS[crack.status],
                border: `1px solid ${STATUS_COLORS[crack.status]}40`,
                whiteSpace: 'nowrap',
              }}>
                {crack.status}
              </div>
            </div>

            {/* Expanded content */}
            {selected?.id === crack.id && (
              <div style={{ padding: '0 1rem 1rem', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                <p style={{ fontSize: '0.84rem', color: 'rgba(255,255,255,0.68)', lineHeight: 1.78, margin: '0.875rem 0 0' }}>
                  {isRuach ? crack.ruach : crack.science}
                </p>

                {/* Implication box */}
                <div style={{
                  marginTop: '0.875rem', padding: '0.75rem 1rem', borderRadius: '8px',
                  background: `${crack.color}0d`, border: `1px solid ${crack.color}25`,
                }}>
                  <div style={{ fontSize: '0.62rem', fontFamily: 'Space Mono', textTransform: 'uppercase', letterSpacing: '0.08em', color: crack.color, marginBottom: '0.35rem' }}>
                    {isRuach ? 'Ancient witness' : 'What this means'}
                  </div>
                  <p style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.55)', lineHeight: 1.7, margin: 0 }}>
                    {isRuach ? crack.ruachImplication : crack.implications}
                  </p>
                </div>

                {isRuach && crack.scripture && (
                  <p style={{ fontSize: '0.75rem', color: `${accentColor}70`, fontStyle: 'italic', margin: '0.6rem 0 0', lineHeight: 1.6 }}>
                    {crack.scripture}
                  </p>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Bottom note */}
      <div style={{ marginTop: '1.5rem', padding: '1rem', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.02)' }}>
        <p style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.35)', lineHeight: 1.7, margin: 0 }}>
          {isRuach
            ? '"For my thoughts are not your thoughts, neither are your ways my ways, declares the LORD. As the heavens are higher than the earth, so are my ways higher than your ways and my thoughts than your thoughts." — Isaiah 55:8-9. The Standard Model is humanity\'s greatest scientific achievement. It is also a map drawn at the edge of a continent we have barely begun to explore.'
            : 'The Standard Model has passed every experimental test since the 1970s. It predicted the W boson, Z boson, gluon, top quark, and Higgs boson before they were discovered — all confirmed. Its failures are not failures of precision but failures of completeness. The universe is larger than the model.'}
        </p>
      </div>
    </div>
  );
}
